/**
 * forms request schemas — client↔server contract (src/shared, HS2-HAQYG6).
 * zod-only; the server validates with the schemas, the client imports the
 * inferred `export type` bodies (`import type`, so no zod reaches the SPA bundle).
 */
import { z } from 'zod';

/**
 * The FormRequest schema (docs/4-ai-forms-and-chat.md, Dynamic forms; FR-4.1–4.5).
 * The AI emits one of these through the `request_form` tool; the server validates
 * it, so a malformed or oversized form is rejected and the model asked to re-emit.
 * Field types are a fixed, UI-renderable set — no free-form HTML from the model.
 */
export const FIELD_TYPES = [
  'text',
  'textarea',
  'number',
  'currency',
  'boolean',
  'select',
  'multiselect',
  'date',
  'file',
] as const;

export const MAX_FIELDS = 8;

/** The `reason` stamped on the pricing check's optional clarification form (FR-3.12, HS2-B0AK80). */
export const CLARIFY_FORM_REASON = 'clarify before benchmarking';

/**
 * Whether a form still takes answers: any open form, plus a skipped pricing-check
 * clarification, whose questions stay stated assumptions that can be answered
 * and re-checked later (FR-3.12, HS2-54N88P). Other skipped forms stay closed.
 */
export function formAcceptsAnswers(form: { status: string; reason?: string | null }): boolean {
  return form.status === 'open' || (form.status === 'skipped' && form.reason === CLARIFY_FORM_REASON);
}

const optionSchema = z.object({ value: z.string(), label: z.string() });

const relatesToSchema = z.object({
  entity: z.enum(['lineItem', 'quotation', 'document']),
  id: z.string(),
});

export const formFieldSchema = z.object({
  key: z.string().min(1).max(64),
  label: z.string().min(1).max(400),
  type: z.enum(FIELD_TYPES),
  options: z.array(optionSchema).max(30).optional(),
  optionsFrom: z.string().optional(), // e.g. 'org.sites'
  required: z.boolean().optional(),
  help: z.string().max(400).optional(),
  default: z.union([z.string(), z.number(), z.boolean()]).optional(),
  relatesTo: relatesToSchema.optional(),
  saveTo: z.string().optional(), // persist the answer as a context entry / org setting
});
export type FormField = z.infer<typeof formFieldSchema>;

export const formRequestSchema = z.object({
  title: z.string().min(1).max(200),
  intro: z.string().max(600).optional(),
  targetRole: z.enum(['reviewer', 'requester', 'procurement', 'any']).default('reviewer'),
  reason: z.string().max(120).optional(),
  fields: z.array(formFieldSchema).min(1).max(MAX_FIELDS),
  allowSkip: z.boolean().optional(),
});
export type FormRequestSpec = z.infer<typeof formRequestSchema>;

/** Answers keyed by field key; a file field's value is an uploaded document id. */
export const formAnswerSchema = z.object({
  answers: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.array(z.string())])),
  skippedFields: z.array(z.string()).optional(),
});
export type FormAnswer = z.infer<typeof formAnswerSchema>;

/** A partial, per-member draft. Numeric/currency inputs remain strings until
 * final submission so unfinished values can round-trip without coercion. */
export const formDraftSchema = z.object({
  answers: z
    .record(z.string().min(1).max(64), z.union([z.string().max(10_000), z.boolean()]))
    .refine((answers) => Object.keys(answers).length <= MAX_FIELDS, `at most ${String(MAX_FIELDS)} draft fields`),
});
export type FormDraft = z.infer<typeof formDraftSchema>;

export const formRequestRowSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  conversationId: z.string().nullable(),
  demandId: z.string().nullable(),
  schema: formRequestSchema,
  targetRole: z.string().nullable(),
  reason: z.string().nullable(),
  status: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type FormRequestRowT = z.infer<typeof formRequestRowSchema>;
export const demandFormsResponseSchema = z.object({ forms: z.array(formRequestRowSchema) });
export type DemandFormsResponse = z.infer<typeof demandFormsResponseSchema>;
export const formDraftResponseSchema = z.object({ answers: formDraftSchema.shape.answers.nullable() });
export type FormDraftResponse = z.infer<typeof formDraftResponseSchema>;
export const savedFormDraftResponseSchema = z.object({ answers: formDraftSchema.shape.answers });
export type SavedFormDraftResponse = z.infer<typeof savedFormDraftResponseSchema>;
const assumptionSchema = z.object({ key: z.string(), label: z.string() });
export const formAnswerResponseSchema = z.object({
  formId: z.string(),
  answers: z.record(z.string(), z.unknown()),
  assumptions: z.array(assumptionSchema),
  savedFacts: z.array(z.object({ id: z.string(), text: z.string() })),
  resumedJobId: z.string().optional(),
});
export type FormAnswerResponse = z.infer<typeof formAnswerResponseSchema>;
export const formSkipResponseSchema = z.object({
  assumptions: z.array(assumptionSchema),
  resumedJobId: z.string().optional(),
});
export type FormSkipResponse = z.infer<typeof formSkipResponseSchema>;
export const contextEntriesResponseSchema = z.object({
  entries: z.array(z.object({ id: z.string(), text: z.string(), sourceKind: z.string() })),
});
export type ContextEntriesResponse = z.infer<typeof contextEntriesResponseSchema>;
